import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Modal,
  Box,
  TextField,
  Stack,
  Typography,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import config from '../config';

const server_URL = config.SERVER_URL;

const Events = () => {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [eventForm, setEventForm] = useState({
    method: 'add',
    name: '',
    key: '',
    description: '',
  });
  const [events, setEvents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState('');
  const [displayFilter, setDisplayFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loggedUserId, setLoggedUserId] = useState(null);
  const [toastState, setToastState] = useState({ open: false, message: '', severity: 'info' });

  const showToast = (message, severity = 'info') => {
    setToastState({ open: true, message, severity });
  };

  const generateKeyFromName = (name) => {
    if (!name) return '';
    
    // Replace spaces with underscores, keep only alphanumeric and underscore, convert to uppercase
    let key = name
      .replace(/\s+/g, '_')  // Replace spaces with underscores
      .replace(/[^A-Za-z0-9_]/g, '')  // Remove everything except alphanumeric and underscore
      .replace(/_+/g, '_')  // Replace multiple underscores with single underscore
      .replace(/^_|_$/g, '')  // Remove leading/trailing underscores
      .toUpperCase();  // Convert to uppercase
    
    return key;
  };

  const handleOpen = () => {
    setEventForm({
      method: 'add',
      name: '',
      key: '',
      description: '',
    });
    setEditingId(null);
    setOpen(true);
  };

  const handleClose = () => {
    setEventForm({
      method: 'add',
      name: '',
      key: '',
      description: '',
    });
    setEditingId(null);
    setOpen(false);
  };

  const handleEdit = (event) => {
    setEventForm({
      method: 'update',
      name: event.name,
      key: event.key,
      description: event.description || '',
    });
    setEditingId(event.id);
    setOpen(true);
  };

  useEffect(() => {
    fetchEvents();
    // Get logged user info
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role_id || null);
    setLoggedUserId(userData.id || null);
    
    // Fetch customers if role 1
    if (userData.role_id === 1) {
      fetchCustomers();
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [customerFilter, displayFilter]);

  const fetchCustomers = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          method: 'list',
          user_id: userData.id,
          role_id: userData.role_id
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCustomers(data.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchEvents = async () => {
    setIsLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'list',
        user_id: userData.id,
        user_role: userData.role_id,
      };
      
      // Add customer filter if provided (only for role 1)
      if (userData.role_id === 1 && customerFilter !== null && customerFilter !== '') {
        requestBody.customer_id = customerFilter;
      }
      
      // Add display filter (all/common) - only for role 1
      if (userData.role_id === 1 && displayFilter === 'common') {
        requestBody.display_filter = 'common';
      }
      
      const response = await fetch(server_URL + '/api/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setEvents(data.data);
      } else {
        showToast(data.error || 'Failed to fetch events', 'error');
        setEvents([]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      showToast('Error fetching events', 'error');
      setEvents([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setEventForm(prev => {
      const updated = { ...prev, [field]: value };
      // Auto-generate key when name changes (always, since key is non-editable)
      if (field === 'name') {
        updated.key = generateKeyFromName(value);
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!eventForm.name || !eventForm.name.trim()) {
      showToast('Event name is required', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestData = {
        ...eventForm,
        user_id: userData.id,
        user_role: userData.role_id,
        is_subuser: userData.is_subuser || false,
        subuser_id: userData.subuser_id || null,
      };

      if (editingId) {
        requestData.id = editingId;
      }

      const response = await fetch(server_URL + '/api/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();
      if (data.status === '1') {
        showToast(data.message || (editingId ? 'Event updated successfully' : 'Event added successfully'), 'success');
        handleClose();
        fetchEvents();
      } else {
        showToast(data.error || 'Failed to save event', 'error');
      }
    } catch (error) {
      console.error('Error saving event:', error);
      showToast('Error saving event', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    setDeletingId(id);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'delete',
          id: id,
          user_id: userData.id,
          user_role: userData.role_id,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });

      const data = await response.json();
      if (data.status === '1') {
        showToast(data.message || 'Event deleted successfully', 'success');
        fetchEvents();
      } else {
        showToast(data.error || 'Failed to delete event', 'error');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      showToast('Error deleting event', 'error');
    } finally {
      setDeletingId(null);
    }
  };


  return (
    <div style={{ padding: '20px' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Events
        </Typography>
        <Button
          variant="contained"
          startIcon={<FaPlus />}
          onClick={handleOpen}
          sx={{ backgroundColor: '#1976d2' }}
        >
          Add Event
        </Button>
      </Stack>

      {/* Customer Filter - Only show for role 1 (admin) */}
      {userRole === 1 && (
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          alignItems: 'center', 
          mb: 3,
          p: 2,
          bgcolor: '#f5f5f5',
          borderRadius: 1
        }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Display</InputLabel>
            <Select
              value={displayFilter}
              onChange={(e) => setDisplayFilter(e.target.value)}
              label="Display"
              size="small"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="common">Common</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Filter by Customer</InputLabel>
            <Select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              label="Filter by Customer"
              size="small"
            >
              <MenuItem value="">All Customers</MenuItem>
              {customers.filter(customer => customer.id !== 0).map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.username} ({customer.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(customerFilter !== '' || displayFilter !== 'all') && (
            <Button 
              variant="outlined"
              color="warning"
              onClick={() => {
                setCustomerFilter('');
                setDisplayFilter('all');
              }}
              sx={{ height: '40px' }}
            >
              Clear Filter
            </Button>
          )}
          <Button 
            variant="outlined"
            onClick={fetchEvents}
            sx={{ height: '40px' }}
          >
            Refresh
          </Button>
        </Box>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Event Name</strong></TableCell>
                <TableCell><strong>Key</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                {userRole === 1 && <TableCell><strong>Customer</strong></TableCell>}
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={userRole === 1 ? 5 : 4} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No events found. Click "Add Event" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                events.map((event) => {
                  // Check if role 2 user can edit/delete this event
                  // Role 2 users cannot edit/delete role 1 events
                  const isRole1Event = event.creator_role_id === 1;
                  const canEdit = userRole === 1 || (userRole === 2 && !isRole1Event && event.created_by === loggedUserId);
                  const canDelete = userRole === 1 || (userRole === 2 && !isRole1Event && event.created_by === loggedUserId);
                  
                  return (
                    <TableRow key={event.id}>
                      <TableCell>{event.name}</TableCell>
                      <TableCell>
                        <code style={{ backgroundColor: '#f5f5f5', padding: '4px 8px', borderRadius: '4px' }}>
                          {event.key}
                        </code>
                      </TableCell>
                      <TableCell>
                        {event.description || <span style={{ color: '#999' }}>No description</span>}
                      </TableCell>
                      {userRole === 1 && (
                        <TableCell>
                          {event.creator_username || 'N/A'}
                          {event.creator_email && (
                            <Typography variant="caption" display="block" sx={{ color: '#666', fontSize: '0.75rem' }}>
                              {event.creator_email}
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {canEdit ? (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(event)}
                              title="Edit"
                            >
                              <FaEdit />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              color="primary"
                              disabled
                              title="Cannot edit admin-created events"
                            >
                              <FaEdit />
                            </IconButton>
                          )}
                          {canDelete ? (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(event.id)}
                              disabled={deletingId === event.id}
                              title="Delete"
                            >
                              {deletingId === event.id ? <CircularProgress size={16} /> : <FaTrash />}
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              color="error"
                              disabled
                              title="Cannot delete admin-created events"
                            >
                              <FaTrash />
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Modal */}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="event-modal-title"
        aria-describedby="event-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '500px' },
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography id="event-modal-title" variant="h6" component="h2" mb={2}>
            {editingId ? 'Edit Event' : 'Add Event'}
          </Typography>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                label="Event Name"
                value={eventForm.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
                fullWidth
                disabled={isSubmitting}
              />
              <TextField
                label="Key"
                value={eventForm.key}
                disabled
                fullWidth
                helperText="Key is auto-generated from event name (uppercase, spaces replaced with underscores)"
                sx={{
                  '& .MuiInputBase-input': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
              <TextField
                label="Description"
                value={eventForm.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                fullWidth
                multiline
                rows={3}
                disabled={isSubmitting}
              />
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
                >
                  {isSubmitting ? 'Saving...' : editingId ? 'Update' : 'Add'}
                </Button>
              </Stack>
            </Stack>
          </form>
        </Box>
      </Modal>

      {/* Toast Notification */}
      <Snackbar
        open={toastState.open}
        autoHideDuration={6000}
        onClose={() => setToastState({ ...toastState, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToastState({ ...toastState, open: false })}
          severity={toastState.severity}
          sx={{ width: '100%' }}
        >
          {toastState.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Events;

